import { Building2, UserMinus, UserPlus } from "lucide-react";
import { useMemo, useState } from "react";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { SearchInput } from "@/components/shared/search-input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import {
  type PersonalOffice,
  useAssignPersonalOffice,
  usePersonalOffices,
} from "@/hooks/use-personal-offices";
import { type User, useUsers } from "@/hooks/use-users";

type AssignDialogState = { type: "none" } | { type: "assign"; office: PersonalOffice };

export function OfficesTable() {
  const { data: offices = [], isLoading, isError, error, refetch } = usePersonalOffices();
  const assign = useAssignPersonalOffice();
  const [dialog, setDialog] = useState<AssignDialogState>({ type: "none" });
  const [userSearch, setUserSearch] = useState("");
  const debouncedSearch = useDebouncedValue(userSearch, 300);
  const { data: users = [], isLoading: usersLoading } = useUsers({
    search: dialog.type === "assign" ? debouncedSearch : undefined,
  });

  const assignedUserIds = useMemo(
    () => new Set(offices.map((o) => o.ownerUserId).filter((id): id is string => !!id)),
    [offices],
  );

  if (isError) {
    return (
      <ErrorState message={error?.message ?? "Failed to load offices"} onRetry={() => refetch()} />
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Assign each enclosed office pod to a teammate. Owners can open or close their door and
        customize the plaque from the virtual office.
      </p>

      <div className="overflow-hidden rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-left text-muted-foreground">
            <tr>
              <th className="px-4 py-3 font-medium">Office</th>
              <th className="px-4 py-3 font-medium">Owner</th>
              <th className="px-4 py-3 font-medium">Door</th>
              <th className="px-4 py-3 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading
              ? ["sk-a", "sk-b", "sk-c", "sk-d", "sk-e", "sk-f", "sk-g", "sk-h"].map((key) => (
                  <tr key={key} className="border-t border-border">
                    <td className="px-4 py-3" colSpan={4}>
                      <div className="h-4 w-full animate-pulse rounded bg-muted" />
                    </td>
                  </tr>
                ))
              : null}
            {!isLoading && offices.length === 0 ? (
              <tr>
                <td colSpan={4} className="p-8">
                  <EmptyState
                    icon={Building2}
                    title="No offices"
                    description="Personal office pods have not been seeded yet."
                  />
                </td>
              </tr>
            ) : null}
            {offices.map((office) => (
              <tr key={office.id} className="border-t border-border">
                <td className="px-4 py-3">
                  <div className="font-medium text-foreground">{office.displayName}</div>
                  <div className="text-xs text-muted-foreground">{office.slotKey}</div>
                </td>
                <td className="px-4 py-3">
                  {office.ownerName ? (
                    <div>
                      <div className="font-medium">{office.ownerName}</div>
                      <div className="text-xs text-muted-foreground">{office.ownerEmail}</div>
                    </div>
                  ) : (
                    <span className="text-muted-foreground">Unassigned</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <Badge variant={office.doorStatus === "open" ? "default" : "secondary"}>
                    {office.doorStatus === "open" ? "Open" : "Closed"}
                  </Badge>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setUserSearch("");
                        setDialog({ type: "assign", office });
                      }}
                    >
                      <UserPlus className="mr-1.5 h-3.5 w-3.5" />
                      Assign
                    </Button>
                    {office.ownerUserId ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={assign.isPending}
                        onClick={() => assign.mutate({ id: office.id, ownerUserId: null })}
                      >
                        <UserMinus className="mr-1.5 h-3.5 w-3.5" />
                        Unassign
                      </Button>
                    ) : null}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog
        open={dialog.type === "assign"}
        onOpenChange={(open) => {
          if (!open) setDialog({ type: "none" });
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              Assign {dialog.type === "assign" ? dialog.office.displayName : "office"}
            </DialogTitle>
          </DialogHeader>
          <SearchInput
            value={userSearch}
            onValueChange={setUserSearch}
            placeholder="Search users by email…"
          />
          <div className="max-h-64 space-y-1 overflow-y-auto">
            {usersLoading ? (
              <p className="px-2 py-4 text-sm text-muted-foreground">Loading users…</p>
            ) : users.length === 0 ? (
              <p className="px-2 py-4 text-sm text-muted-foreground">No users found.</p>
            ) : (
              users.map((u: User) => {
                const takenElsewhere =
                  assignedUserIds.has(u.id) &&
                  dialog.type === "assign" &&
                  dialog.office.ownerUserId !== u.id;
                return (
                  <button
                    key={u.id}
                    type="button"
                    disabled={assign.isPending}
                    onClick={() => {
                      if (dialog.type !== "assign") return;
                      assign.mutate(
                        { id: dialog.office.id, ownerUserId: u.id },
                        { onSuccess: () => setDialog({ type: "none" }) },
                      );
                    }}
                    className="flex w-full items-center justify-between rounded-md px-3 py-2 text-left hover:bg-muted"
                  >
                    <span>
                      <span className="block text-sm font-medium">{u.name}</span>
                      <span className="block text-xs text-muted-foreground">{u.email}</span>
                    </span>
                    {takenElsewhere ? (
                      <span className="text-[10px] font-semibold tracking-wide text-muted-foreground">
                        REASSIGN
                      </span>
                    ) : null}
                  </button>
                );
              })
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog({ type: "none" })}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
